import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { formatNumber } from '@vy/utils';
import type { ChartDataKey } from '../../../hooks/use-customer-portal-chart-data-utils';
import { CustomerPortalChartTooltip } from './CustomerPortalChartTooltip';
import { formatMonthLabel } from './CustomerPortalUtils.ts';

export type CustomerPortalChartSeriesBase = {
  dataKey: string;
  label: string;
};

export type CustomerPortalChartSeries = CustomerPortalChartSeriesBase & {
  type: 'bar';
  color: string;
  stack: string;
};

export type CustomerPortalChartSeriesItem = CustomerPortalChartSeriesBase & {
  data: (number | null)[];
  id?: string | number;
};

export type ChartDataRow = Record<string, string | number | null>;

type CustomerPortalChartProps = {
  chartData: ChartDataRow[];
  chartSeries: CustomerPortalChartSeries[];
  xAxisValueFormatter?: (value: string) => string;
  tooltipTitle?: string;
  barWidthTrim?: number;
  chartMargins?: { top?: number; right?: number; bottom?: number; left?: number };
  xAxisDataKey: ChartDataKey;
};

export const CustomerPortalChart: React.FC<CustomerPortalChartProps> = ({
  chartData,
  chartSeries,
  xAxisValueFormatter = formatMonthLabel,
  tooltipTitle = 'Usage Data',
  barWidthTrim = 10,
  chartMargins = { left: 95, right: 20, top: 20, bottom: 20 },
  xAxisDataKey,
}) => {
  const setBarStyle = (widthTrim: number) => {
    return { clipPath: `inset(0px ${widthTrim}px 0px ${widthTrim}px)` };
  };

  return (
    <ChartsContainer
      dataset={chartData}
      xAxis={[
        {
          scaleType: 'band',
          dataKey: xAxisDataKey,
          tickLabelStyle: {
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fill: '#323232',
          },
          valueFormatter: xAxisValueFormatter,
        },
      ]}
      yAxis={[
        {
          tickLabelStyle: {
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fill: '#323232',
          },
          valueFormatter: (value: number) => (value === 0 ? '0' : formatNumber(value)),
        },
      ]}
      series={chartSeries}
      height={400}
      margin={chartMargins}
      sx={{
        '& .MuiChartsBar-root': {
          transition: 'fill 0.2s ease-in-out',
        },
      }}
    >
      <BarPlot
        borderRadius={2}
        slotProps={{
          bar: setBarStyle(barWidthTrim),
        }}
      />
      <ChartsXAxis />
      <ChartsYAxis />
      <ChartsTooltipContainer trigger="axis">
        <CustomerPortalChartTooltip title={tooltipTitle} />
      </ChartsTooltipContainer>
    </ChartsContainer>
  );
};
